import React, { useState, useEffect } from 'react';
import { 
  Check, Star, Shield, Users, BarChart3, 
  MessageSquare, Zap, Globe, ChevronDown,
  X, Filter, Smartphone, 
  QrCode, ShieldCheck, Lock, Menu, ChevronRight, ArrowRight
} from 'lucide-react';

import './HomepageV2.css';
import CookieConsent from './components/CookieConsent';
import SeoHead from './components/SeoHead';
import CaseStudyShowcase from './components/CaseStudyShowcase';

const HomepageV2 = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showScreenshots, setShowScreenshots] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const colors = {
    primary: '#1e3a8a',
    secondary: '#60a5fa',
    accent: '#f97316',
    success: '#10b981',
    dark: '#1a1a2e',
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Brauchen meine Mitarbeiter eine Schulung?",
      answer: "Nein. Ihre Mitarbeiter müssen lediglich QR-Code-Visitenkarten ausgeben oder auf ausgehängte QR-Codes hinweisen. Das System funktioniert vollautomatisch im Hintergrund."
    },
    {
      question: "Ist das rechtlich sicher (DSGVO)?",
      answer: "Ja, zu 100%. smartRATINGS ist vollständig DSGVO-konform, entwickelt und gehostet in Deutschland. Alle Daten werden auf deutschen Servern gespeichert und verarbeitet."
    },
    {
      question: "Wie schnell amortisiert sich die Investition?",
      answer: "Die Investition in smartRATINGS amortisiert sich typischerweise nach 6-8 Wochen durch zusätzliche Patientenanfragen. Bereits 1-2 neue Patienten pro Monat decken die monatliche Investition und generieren darüber hinaus nachhaltigen Mehrwert für Ihre Praxis."
    },
    {
      question: "Was kostet smartRATINGS?",
      answer: "Die Investition beginnt bei 249€ monatlich und skaliert je nach Praxis-Größe und gewünschtem Funktionsumfang. Im kostenlosen Beratungsgespräch berechnen wir gemeinsam Ihr individuelles Angebot – transparent und fair."
    },
    {
      question: "Was ist, wenn es nicht funktioniert?",
      answer: "Sie sind durch unsere 90-Tage Erfolgsgarantie abgesichert. Voraussetzung: Sie setzen smartRATINGS aktiv ein (QR-Codes werden mindestens 10-mal gescannt). Wenn Sie trotzdem nicht zufrieden sind, erhalten Sie die komplette Lizenzgebühr zurück."
    }
  ];

  return (
    <div className="homepage-v2">
      <SeoHead
        title="Professionelles Bewertungsmanagement für lokale Unternehmen"
        description="smartRATINGS: Negative Bewertungen abfangen, positive intelligent auf Google & Co. verteilen. KI-gestützte Lösung für Arztpraxen und lokale Unternehmen."
      />
      <CookieConsent />
      
      {/* Navigation */}
      <nav className={`navigation ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="logo">
            <img 
              src="/smartRATINGS_ai-logo.png" 
              alt="smartRATINGS" 
              className="logo-image"
            />
          </a>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
          
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#wie-funktioniert">Wie es funktioniert</a>
            <a href="#features">Features</a>
            <a href="#faq">FAQ</a>
            <a href="/termin/" className="nav-cta">Kostenlose Beratung</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-v2">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-headline">
              Schlechte Bewertungen<br />
              kosten Sie Patienten.<br />
              Gute bleiben aus.<br />
              <span className="highlight-problem">Das ändert sich jetzt.</span>
            </h1>
            
            <p className="hero-subline">
              smartRATINGS generiert gezielt echte Bewertungen, verteilt diese 
              automatisch und verhindert, dass negative online gehen.
            </p>
            
            <div className="hero-cta">
              <a href="/termin" className="btn-primary-large">
                15-Minuten Analyse buchen – unverbindlich
                <ChevronRight size={20} />
              </a>
            </div>
            
            <div className="hero-trust-badges">
              <div className="trust-badge">
                <ShieldCheck size={20} color={colors.success} />
                <span>100% DSGVO-konform</span>
              </div>
              <div className="trust-badge">
                <svg width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: '2px', flexShrink: 0 }}>
                  <rect width="20" height="4.67" y="0" fill="#000000" />
                  <rect width="20" height="4.67" y="4.67" fill="#DD0000" />
                  <rect width="20" height="4.67" y="9.33" fill="#FFCE00" />
                </svg>
                <span>Made in Germany</span>
              </div>
              <div className="trust-badge">
                <Check size={20} color={colors.success} />
                <span>Keine Mindestlaufzeit</span>
              </div>
            </div>

            {/* Comparison Graphic */}
            <div className="hero-comparison">
              <p className="comparison-platforms">Integriert mit führenden Bewertungsplattformen wie u.a.</p>
              <div className="platform-logos">
                <span className="platform-logo">Google</span>
                <span className="platform-logo">jameda</span>
                <span className="platform-logo">Sanego</span>
                <span className="platform-logo">DocInsider</span>
                <span className="platform-logo">Proven Expert</span>
                <span className="platform-logo">uvm.</span>
              </div>
              
              <div className="comparison-boxes">
                <div className="comparison-box before">
                  <p className="comparison-label">Ihre Praxis heute</p>
                  <div className="comparison-rating">
                    <div className="rating-display">
                      <span className="rating-number">3.8</span>
                      <div className="rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            size={20} 
                            fill={star <= 4 ? '#f59e0b' : '#d1d5db'} 
                            stroke={star <= 4 ? '#f59e0b' : '#d1d5db'}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="rating-consequence">Verliert Patienten</p>
                    <p className="rating-detail">an besser bewertete Kollegen</p>
                  </div>
                </div>

                <div className="comparison-arrow">
                  <ArrowRight size={32} />
                  <p className="arrow-label">in 90 Tagen</p>
                </div>

                <div className="comparison-box after">
                  <p className="comparison-label">Mit smartRATINGS</p>
                  <div className="comparison-rating">
                    <div className="rating-display">
                      <span className="rating-number">4.7</span>
                      <div className="rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            size={20} 
                            fill="#10b981" 
                            stroke="#10b981"
                          />
                        ))}
                      </div>
                    </div>
                    <p className="rating-consequence">Top 3 bei Google</p>
                    <p className="rating-detail">Deutlich mehr Patientenanfragen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Qualification Section */}
      <section id="qualification" className="qualification-section animate-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <h2>Sie sind hier richtig, wenn...</h2>
          </div>
          
          <div className="qualification-grid">
            <div className="qualification-item">
              <Check size={48} color={colors.success} />
              <p>Sie unter 4,5 Sterne bei Google haben</p>
            </div>
            
            <div className="qualification-item">
              <Check size={48} color={colors.success} />
              <p>Sie weniger als 50 Bewertungen haben</p>
            </div>
            
            <div className="qualification-item">
              <Check size={48} color={colors.success} />
              <p>Ihre Konkurrenten besser dastehen</p>
            </div>
            
            <div className="qualification-item">
              <Check size={48} color={colors.success} />
              <p>Sie das ändern wollen – ohne Zeitaufwand für Ihr Team</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="wie-funktioniert" className="how-it-works-section animate-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <h2>Wie smartRATINGS funktioniert</h2>
            <p className="section-subtitle">
              Einfach. Automatisch. Effektiv.
            </p>
          </div>
          
          <div className="how-steps">
            <div className="how-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Patient scannt QR-Code</h3>
                <p>
                  Nach der Behandlung erhält Ihr Patient eine Visitenkarte 
                  mit QR-Code oder sieht den Code auf einem Plakat oder Roll-Up 
                  in Ihren Räumlichkeiten.
                </p>
              </div>
            </div>
            
            <div className="step-arrow">→</div>
            
            <div className="how-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Intelligente Filterung</h3>
                <p>
                  Der Patient gibt zunächst eine Sterne-Bewertung ab. 
                  Positive (≥4★) werden zu öffentlichen Portalen geleitet, 
                  negative (≤3★) landen als internes Feedback bei Ihnen.
                </p>
              </div>
            </div>
            
            <div className="step-arrow">→</div>
            
            <div className="how-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Automatische Verteilung</h3>
                <p>
                  Positive Bewertungen werden automatisch auf die richtigen 
                  Portale verteilt (Google, Jameda, etc.). Negative Bewertungen 
                  landen NIE online.
                </p>
              </div>
            </div>
          </div>
          
          <div className="how-cta">
            <a href="/termin" className="btn-primary">
              Kostenlose Beratung vereinbaren
            </a>
          </div>

          {/* Screenshots Toggle */}
          <div className="screenshots-toggle-container">
            <button 
              className="screenshots-toggle-btn"
              onClick={() => setShowScreenshots(!showScreenshots)}
            >
              <span className="toggle-icon">{showScreenshots ? '▲' : '▼'}</span>
              <span className="toggle-text">
                {showScreenshots ? 'Screenshots ausblenden' : 'So sieht es in der Praxis aus'}
              </span>
            </button>
          </div>

          {/* Screenshots Gallery */}
          {showScreenshots && (
            <div className="screenshots-gallery">
              <div className="screenshot-item">
                <div className="screenshot-image-wrapper">
                  <img 
                    src="/images/roll-up.png" 
                    alt="smartRATINGS Roll-Up in der Praxis" 
                    className="screenshot-img"
                  />
                </div>
                <h4>In Ihrer Praxis</h4>
                <p>Professionelle Drucksachen mit QR-Code für Wartezimmer und Empfang</p>
              </div>

              <div className="screenshot-item">
                <div className="screenshot-image-wrapper">
                  <img 
                    src="/images/widget-iPhone.png" 
                    alt="smartRATINGS Bewertungs-Widget auf iPhone" 
                    className="screenshot-img"
                  />
                </div>
                <h4>Patienten bewerten direkt auf ihrem Smartphone</h4>
                <p>Intuitives Bewertungs-Widget - keine App-Installation nötig</p>
              </div>

              <div className="screenshot-item">
                <div className="screenshot-image-wrapper">
                  <img 
                    src="/images/Dashboard.png" 
                    alt="smartRATINGS Dashboard" 
                    className="screenshot-img"
                  />
                </div>
                <h4>Volle Kontrolle & Transparenz</h4>
                <p>Alle Bewertungen und Analysen auf einen Blick in Ihrem persönlichen Dashboard</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Case-Study Showcase — echte Beta-Tester mit Live-Daten.
          Customer-Liste wird dynamisch vom Backend geladen (Public-Endpoint).
          Sichtbar werden alle Customer mit akzeptierter Werbevereinbarung
          und vom Admin explizit für die Website freigegeben (Configurator-Toggle).
          Limits: 3 Cards auf Desktop, 2 auf Mobile. Bei mehr: Link zu
          /erfolgsgeschichten für die vollständige Übersicht. */}
      <CaseStudyShowcase
        title="So sieht echte Veränderung aus"
        subtitle="Live-Daten unserer ersten Partner-Praxen. Bewertungen, die in den ersten Wochen mit smartRATINGS hinzukamen — vollautomatisch."
        maxOnDesktop={3}
        maxOnMobile={2}
        placeholderFillTo={3}
        expectedCount={3}
        seeMoreLink="/erfolgsgeschichten"
        seeMoreLabel="Mehr Erfolgsgeschichten →"
      />

      {/* USP Section */}
      <section id="usp" className="usp-section animate-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <h2>Warum smartRATINGS anders ist</h2>
            <p className="section-subtitle">
              Bisherige Lösungen bekämpfen Symptome – smartRATINGS verhindert das Problem
            </p>
          </div>
          
          <div className="usp-comparison">
            <div className="comparison-row">
              <div className="comparison-item other">
                <X size={24} color="#ef4444" />
                <div className="comparison-text">
                  <h4>Andere Systeme bitten um Bewertungen</h4>
                  <p>Negative landen trotzdem online</p>
                </div>
              </div>
              
              <div className="comparison-item smartratings">
                <Check size={24} color={colors.success} />
                <div className="comparison-text">
                  <h4>smartRATINGS filtert BEVOR bewertet wird</h4>
                  <p>Negative Bewertungen landen NIE online</p>
                </div>
              </div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-item other">
                <X size={24} color="#ef4444" />
                <div className="comparison-text">
                  <h4>Andere Systeme reagieren auf negative Bewertungen</h4>
                  <p>Schaden ist bereits entstanden</p>
                </div>
              </div>
              
              <div className="comparison-item smartratings">
                <Check size={24} color={colors.success} />
                <div className="comparison-text">
                  <h4>smartRATINGS heilt automatisch</h4>
                  <p>Externe negative Bewertungen werden ausgeglichen</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="usp-result">
            <h3>Das Ergebnis:</h3>
            <p>
              Mehr positive Bewertungen, weniger negative – ohne Risiko, 
              ohne Manipulation, ohne Aufwand für Ihr Team.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section animate-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <h2>Das smartRATINGS System</h2>
            <p className="section-subtitle">
              Die Features, die Sie zum Marktführer machen
            </p>
          </div>
          
          <div className="features-grid">
            {/* Feature 1 */}
            <div className="feature-card">
              <div className="feature-icon">
                <Star size={32} color={colors.primary} />
              </div>
              <h3>Mehr Neupatienten durch besseres Rating</h3>
              <p>
                Per SMS und E-Mail werden zufriedene Patienten nach der 
                Behandlung automatisch um eine Bewertung gebeten.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={32} color={colors.primary} />
              </div>
              <h3>Schlechte Bewertungen erreichen Google nie</h3>
              <p>
                Unzufriedene Patienten werden erkannt und intern weitergeleitet – 
                bevor eine schlechte Bewertung öffentlich wird.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="feature-card feature-card-premium">
              <div className="feature-icon">
                <BarChart3 size={32} color={colors.accent} />
              </div>
              <h3>Sehen Sie sofort wo Sie gegenüber Mitbewerbern stehen</h3>
              <p>
                Dashboard mit Echtzeit-Vergleich Ihres Ratings gegenüber 
                direkten Mitbewerbern – inklusive Benchmarks.
              </p>
              <span className="feature-badge badge-premium">*nur PROFESSIONAL Paket</span>
            </div>
            
            {/* Feature 4 */}
            <div className="feature-card feature-card-premium">
              <div className="feature-icon">
                <Zap size={32} color={colors.accent} />
              </div>
              <h3>Selbstheilungsfunktion</h3>
              <p>
                Sie haben von extern eine negative Bewertung erhalten? Kein Problem! 
                smartRATINGS erkennt das und gleicht automatisch mit positiven 
                Bewertungen aus.
              </p>
              <span className="feature-badge badge-premium">*nur PROFESSIONAL Paket</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section animate-on-scroll">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <ShieldCheck size={48} color={colors.success} />
              <h3>100% DSGVO-konform</h3>
              <p>
                Entwickelt in Deutschland, gehostet in Deutschland. 
                Keine rechtlichen Risiken.
              </p>
            </div>
            
            <div className="trust-item">
              <Shield size={48} color={colors.success} />
              <h3>Made in Germany</h3>
              <p>
                Alle Daten werden auf deutschen Servern gespeichert 
                und verarbeitet.
              </p>
            </div>
            
            <div className="trust-item">
              <Check size={48} color={colors.success} />
              <h3>90-Tage Erfolgsgarantie</h3>
              <p>
                Nicht zufrieden? Geld zurück – ohne Wenn und Aber.
              </p>
            </div>
            
            <div className="trust-item">
              <Lock size={48} color={colors.success} />
              <h3>Keine Mindestlaufzeit</h3>
              <p>
                Sie zahlen monatlich und können jederzeit ohne weitere 
                Verpflichtungen kündigen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section animate-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <h2>Häufig gestellte Fragen</h2>
          </div>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className={`faq-question ${openFaq === index ? 'active' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    size={24} 
                    className={`faq-icon ${openFaq === index ? 'rotated' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2>Stoppen Sie den Patientenverlust. Buchen Sie jetzt Ihre Analyse.</h2>
            <p>
              In nur 15 Minuten zeigen wir Ihnen, wie Sie Ihr Bewertungsprofil 
              systematisch verbessern – ohne Manipulation, ohne Risiko.
            </p>
            <a href="/termin" className="btn-primary-large">
              15-Minuten Analyse buchen – unverbindlich
              <ChevronRight size={20} />
            </a>
            <p className="final-cta-note">
              Keine Verpflichtungen. Keine versteckten Kosten.<br />
              Nur eine ehrliche Analyse Ihrer aktuellen Situation.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img 
                  src="/smartRATINGS_ai-logo.png" 
                  alt="smartRATINGS" 
                  className="footer-logo-image"
                />
              </div>
              <p>
                Professionelles Bewertungsmanagement<br />
                für Arztpraxen und lokale Unternehmen
              </p>
              <div className="footer-badge">
                <Shield size={16} color="#10b981" />
                <span>100% DSGVO-konform mit AVV</span>
              </div>
            </div>
            
            <div className="footer-column">
              <div className="footer-section">
                <h4>Produkt</h4>
                <a href="#features">Features</a>
                <a href="#wie-funktioniert">Wie es funktioniert</a>
                <a href="#faq">FAQ</a>
              </div>
              <div className="footer-section footer-section-spacing">
                <h4>Ressourcen</h4>
                <a href="/ratgeber/">Ratgeber</a>
                <a href="/bewertungs-check/">Bewertungs-Check</a>
                <a href="/tools/loeschantrag-generator/">Löschantrag-Generator</a>
                <a href="/tools/antwort-generator/">Antwort-Generator</a>
                <a href="https://support.google.com/business/" target="_blank" rel="noopener noreferrer">Google Unternehmensprofil Hilfe</a>
              </div>
            </div>

            <div className="footer-column">
              <div className="footer-section">
                <h4>Unternehmen</h4>
                <a href="/ueber-uns/">Über uns</a>
                <a href="/kontakt/">Kontakt</a>
                <a href="/termin/">Termin vereinbaren</a>
              </div>
              <div className="footer-section footer-section-spacing">
                <h4>Rechtliches</h4>
                <a href="/impressum/">Impressum</a>
                <a href="/datenschutz/">Datenschutz</a>
                <a href="/agb/">AGB</a>
                <a href="/werbevereinbarung/">Werbevereinbarung</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomepageV2;
